import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  Button, 
  Typography, 
  Space, 
  Modal,
  Form,
  Input,
  Select,
  notification,
  Progress,
  Tabs,
  Row,
  Col,
  Statistic,
  Empty,
  Badge,
  Tag,
  InputNumber,
  TimePicker,
  Rate,
  Checkbox,
  List,
  Avatar,
  Popconfirm
} from 'antd';
import { 
  PlusOutlined,
  EditOutlined,
  CheckCircleOutlined,
  BarChartOutlined,
  FireOutlined,
  LeftOutlined,
  SearchOutlined,
  TrophyOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../api/firebase';
import {
  createHabit,
  getUserHabits,
  updateHabit,
  deleteHabit,
  logHabitCompletion,
  getTodayHabitStatus,
  getHabitAnalytics
} from '../../api/services/habitService';
import {
  HABIT_ICONS,
  HABIT_CATEGORIES,
  HABIT_COLORS,
  HABIT_FREQUENCIES,
  HABIT_TEMPLATES,
  lookupIcon,
  lookupColor,
  lookupCategory,
  getTemplatesByCategory
} from '../../data/masterData';
import './HabitTrackerApp.css';

const { TextArea } = Input;
const { Title, Text } = Typography;
const { Option } = Select;

const HabitTrackerApp = () => {
  const [user] = useAuthState(auth);
  
  // State - cleaned up unused variables
  const [habits, setHabits] = useState([]);
  const [habitStatuses, setHabitStatuses] = useState({});
  const [selectedHabit, setSelectedHabit] = useState(null);
  const [showHabitModal, setShowHabitModal] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completionData, setCompletionData] = useState({ habitId: null, currentValue: 0 });
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('log');
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [creationMode, setCreationMode] = useState('');
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateCategory, setTemplateCategory] = useState('all');
  
  const [form] = Form.useForm();
  const [completionForm] = Form.useForm();

  // Complete responsive styles object
  const responsiveStyles = {
    card: {
      marginBottom: 'clamp(0.75rem, 2.5vw, 1rem)',
      borderRadius: 'clamp(0.75rem, 2vw, 1rem)',
      border: '2px solid transparent',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: '0 0.125rem 0.5rem rgba(0,0,0,0.1)',
      padding: 'clamp(0.75rem, 2.5vw, 1rem)'
    },
    habitIcon: {
      fontSize: 'clamp(1.5rem, 6vw, 2rem)',
      opacity: 1
    },
    completionButton: {
      width: 'clamp(3rem, 10vw, 3.5rem)',
      height: 'clamp(3rem, 10vw, 3.5rem)',
      fontSize: 'clamp(1rem, 4vw, 1.25rem)',
      flexShrink: 0
    },
    modal: {
      width: '95%',
      maxWidth: '50rem'
    },
    templateIcon: {
      fontSize: 'clamp(2rem, 8vw, 2.25rem)',
      marginBottom: 'clamp(0.5rem, 2vw, 0.5rem)'
    },
    cardTitle: {
      margin: 0,
      fontSize: 'clamp(0.75rem, 2.5vw, 0.875rem)',
      fontWeight: 'bold',
      minHeight: 'clamp(2rem, 6vw, 2.25rem)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      lineHeight: 1.2
    },
    bodyText: {
      fontSize: 'clamp(0.625rem, 2vw, 0.6875rem)',
      display: 'block',
      marginBottom: 'clamp(0.5rem, 2vw, 0.5rem)',
      lineHeight: 1.3,
      height: 'clamp(1.5rem, 4vw, 1.625rem)',
      overflow: 'hidden'
    },
    primaryButton: {
      width: '100%',
      fontWeight: 'bold',
      fontSize: 'clamp(0.625rem, 2vw, 0.6875rem)',
      padding: 'clamp(0.25rem, 1vw, 0.375rem)'
    }
  };

  // CORE DATA FUNCTIONS
  const loadHabitAnalytics = useCallback(async (habitId) => {
    if (!user) return;
    try {
      const habitAnalytics = await getHabitAnalytics(user.uid, habitId);
      setAnalytics(prev => ({ ...prev, [habitId]: habitAnalytics }));
    } catch (error) {
      console.error(`Error loading analytics for habit ${habitId}:`, error);
    }
  }, [user]);

  const loadHabitStatuses = useCallback(async (habitsToLoad) => {
    if (!user || !habitsToLoad || habitsToLoad.length === 0) return;
    try {
      const statuses = {};
      await Promise.all(habitsToLoad.map(async (habit) => {
        const status = await getTodayHabitStatus(user.uid, habit.id);
        statuses[habit.id] = status;
      }));
      setHabitStatuses(statuses);
    } catch (error) {
      console.error('Error loading habit statuses:', error);
    }
  }, [user]);

  const loadUserHabits = useCallback(async () => {
    if (!user) {
      console.log('❌ No user authenticated, skipping habit loading');
      return;
    }
    try {
      console.log(`📚 Loading habits for user: ${user.uid}`);
      setLoading(true);
      const userHabits = await getUserHabits(user.uid);
      console.log(`✅ Loaded ${userHabits.length} habits for user`);
      setHabits(userHabits);
      
      if (userHabits.length > 0) {
        loadHabitStatuses(userHabits);
        userHabits.forEach(habit => loadHabitAnalytics(habit.id));
      }
    } catch (error) {
      console.error('❌ Error loading habits:', error);
      notification.error({
        message: 'Error Loading Habits',
        description: 'Could not fetch your habits. Please check your connection and try again.'
      });
    } finally {
      setLoading(false);
    }
  }, [user, loadHabitStatuses, loadHabitAnalytics]);

  // Load habits when user changes
  useEffect(() => {
    if (user) {
      console.log('👤 User authenticated, loading habits...');
      loadUserHabits();
    }
  }, [user, loadUserHabits]);

  // Using Local Master Data - No Firebase loading needed
  useEffect(() => {
    console.log('🚀 Component mounted, using local master data...');
    console.log('✅ Local data loaded:', {
      templates: HABIT_TEMPLATES.length,
      icons: Object.keys(HABIT_ICONS).length,
      categories: HABIT_CATEGORIES.length,
      frequencies: HABIT_FREQUENCIES.length,
      colors: HABIT_COLORS.length
    });
  }, []);

  // Enhanced category info lookup using local data
  const getCategoryInfo = (categoryKey) => {
    const category = HABIT_CATEGORIES.find(cat => cat.key === categoryKey);
    if (!category) {
      return {
        key: 'other',
        label: 'Other',
        icon: '🌟',
        color: '#8c8c8c'
      };
    }
    
    return {
      key: category.key,
      label: category.label,
      icon: lookupIcon(category.iconId).emoji,
      color: lookupColor(category.colorId)
    };
  };

  const getFilteredTemplates = () => {
    let filtered = getTemplatesByCategory(templateCategory);
    if (templateSearch.trim()) {
      const search = templateSearch.toLowerCase();
      filtered = filtered.filter(template => 
        template.name.toLowerCase().includes(search) ||
        template.category.toLowerCase().includes(search) ||
        template.description.toLowerCase().includes(search)
      );
    }
    return filtered;
  };

  // Dynamic data - computed from local master data
  const habitCategories = HABIT_CATEGORIES;

  // Helper function to check if habit should be shown today based on frequency
  const shouldShowHabitToday = (habit) => {
    const today = new Date();
    const dayOfWeek = today.getDay();

    switch (habit.frequency) {
      case 'daily':
        return true;
      case 'weekdays':
        return dayOfWeek >= 1 && dayOfWeek <= 5;
      case 'weekends':
        return dayOfWeek === 0 || dayOfWeek === 6;
      case 'weekly':
        return true;
      case 'custom':
        return true;
      default:
        return true;
    }
  };

  const handleCreateHabit = () => {
    setSelectedHabit(null);
    setIsEditing(false);
    setShowHabitModal(true);
    setCreationMode('');
    setShowTemplates(false);
    setTemplateSearch('');
    setTemplateCategory('all');
    form.resetFields();
  };

  const handleUseTemplate = (template) => {
    console.log('🔄 Using template:', template);
    
    // SIMPLIFIED: No units - target count and description handle everything
    const formValues = {
      name: template.name || '',
      icon: template.iconId || 'checkmark',
      category: template.category || 'other',
      color: template.colorId || 'blue',
      frequency: template.frequency || 'daily',
      targetCount: template.targetCount || 1,
      description: template.description || `Track ${template.name?.toLowerCase() || 'this habit'}`,
      isQuantifiable: Boolean(template.isQuantifiable) || (template.targetCount > 1),
      difficulty: template.difficulty || 2,
      reminderTime: template.reminderTime || null
    };
    
    console.log('📝 Setting template form values (NO UNITS):', formValues);
    
    setCreationMode('custom');
    setShowTemplates(false);
    
    setTimeout(() => {
      form.setFieldsValue(formValues);
    }, 100);
  };

  const handleEditHabit = (habit) => {
    console.log('🔄 Editing habit:', habit);
    
    setSelectedHabit(habit);
    setIsEditing(true);
    setCreationMode('custom');
    setShowHabitModal(true);
    
    // Convert existing habit values to IDs using local data
    const findIconIdByEmoji = (emoji) => {
      for (const [iconId, iconData] of Object.entries(HABIT_ICONS)) {
        if (iconData.emoji === emoji) {
          return iconId;
        }
      }
      return 'checkmark';
    };

    const findColorIdByValue = (colorValue) => {
      const colorObj = HABIT_COLORS.find(c => c.value === colorValue);
      return colorObj ? colorObj.id : 'blue';
    };

    // Prepare form values - NO UNITS
    const formValues = {
      name: habit.name || '',
      description: habit.description || '',
      category: habit.category || 'other',
      frequency: habit.frequency || 'daily',
      color: habit.color?.startsWith('#') ? findColorIdByValue(habit.color) : (habit.color || 'blue'),
      icon: habit.icon?.length <= 2 ? findIconIdByEmoji(habit.icon) : (habit.icon || 'checkmark'),
      isQuantifiable: Boolean(habit.isQuantifiable),
      targetCount: habit.targetCount || 1,
      difficulty: habit.difficulty || 2,
      reminderTime: habit.reminderTime || null
    };
    
    form.resetFields();
    setTimeout(() => {
      form.setFieldsValue(formValues);
    }, 100);
  };

  const handleDeleteHabit = async (habitId) => {
    try {
      await deleteHabit(user.uid, habitId);
      notification.success({
        message: 'Habit deleted successfully!'
      });
      await loadUserHabits();
    } catch (error) {
      notification.error({
        message: 'Error deleting habit',
        description: error.message
      });
    }
  };

  const handleHabitSubmit = async (values) => {
    if (loading) return;
    
    if (!user || !user.uid) {
      notification.error({ message: 'Please sign in to save habits' });
      return;
    }

    setLoading(true);
    try {
      // SIMPLIFIED: Store display values directly, NO UNITS
      const sanitizedValues = {
        name: values.name?.trim() || '',
        description: values.description?.trim() || '',
        category: values.category || 'other',
        frequency: values.frequency || 'daily',
        color: lookupColor(values.color || 'blue'),
        icon: lookupIcon(values.icon || 'checkmark').emoji,
        isQuantifiable: Boolean(values.isQuantifiable),
        targetCount: values.isQuantifiable ? Math.max(1, parseInt(values.targetCount) || 1) : 1,
        difficulty: Math.max(1, Math.min(5, values.difficulty || 2)),
        reminderTime: values.reminderTime || null
      };

      console.log('Sanitized values (NO UNITS):', sanitizedValues);

      // Validation
      if (!sanitizedValues.name || sanitizedValues.name.length < 2) {
        throw new Error('Habit name must be at least 2 characters long');
      }
      if (sanitizedValues.name.length > 50) {
        throw new Error('Habit name must be less than 50 characters');
      }

      const existingHabit = habits.find(habit => 
        habit.name.toLowerCase() === sanitizedValues.name.toLowerCase() && 
        (!isEditing || habit.id !== selectedHabit.id)
      );
      
      if (existingHabit) {
        throw new Error('A habit with this name already exists');
      }

      if (isEditing) {
        await updateHabit(user.uid, selectedHabit.id, sanitizedValues);
        notification.success({ message: 'Habit updated successfully!' });
      } else {
        await createHabit(user.uid, sanitizedValues);
        notification.success({ message: 'Habit created successfully!' });
      }
      
      setShowHabitModal(false);
      setCreationMode('');
      setIsEditing(false);
      setSelectedHabit(null);
      form.resetFields();
      
      // Refresh habits
      await loadUserHabits();
      
    } catch (error) {
      console.error('Error in handleHabitSubmit:', error);
      notification.error({
        message: `Error ${isEditing ? 'updating' : 'creating'} habit`,
        description: error.message || 'An unknown error occurred. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCompletion = async (habitId, completed) => {
    try {
      const habit = habits.find(h => h.id === habitId);
      if (!habit) return;

      if (!completed) {
        await logHabitCompletion(user.uid, habitId, new Date(), false);
        setHabitStatuses(prev => ({ ...prev, [habitId]: { completed: false } }));
        notification.info({ message: 'Habit unmarked', description: 'Progress reset for today.' });
      } else if (habit.isQuantifiable) {
        setCompletionData({ habitId, currentValue: 0 });
        setShowCompletionModal(true);
      } else {
        await logHabitCompletion(user.uid, habitId, new Date(), true, { actualValue: 1, targetValue: 1, completionPercentage: 100 });
        setHabitStatuses(prev => ({ ...prev, [habitId]: { completed: true, completionPercentage: 100 } }));
        notification.success({ message: '🎉 Excellent!', description: `${habit.name} completed!`, placement: 'topRight' });
      }
      loadHabitAnalytics(habitId);
    } catch (error) {
      notification.error({ message: 'Error updating habit', description: error.message });
    }
  };

  const getTodaysHabits = () => {
    return habits.filter(habit => shouldShowHabitToday(habit));
  };

  return (
    <div className="habit-tracker-container">
      <div className="habit-tracker-header">
        <div className="habit-tracker-header-row">
          <Title level={2} className="habit-tracker-title">
            🎯 Habit Tracker
          </Title>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleCreateHabit} 
            size="large" 
            className="habit-tracker-add-button"
          >
            Add Habit
          </Button>
        </div>
        <Text type="secondary" className="habit-tracker-subtitle">
          Build better habits, track your progress
        </Text>
      </div>

      <div style={{ marginBottom: 'clamp(1rem, 3vw, 1.25rem)' }}>
        <Title level={3} style={{ 
          color: '#1E90FF', 
          marginBottom: 'clamp(0.75rem, 2.5vw, 1rem)', 
          fontSize: 'clamp(1.125rem, 3.5vw, 1.25rem)', 
          fontWeight: 'bold' 
        }}>
          📊 Today's Progress
        </Title>
        <Row gutter={[{ xs: 8, sm: 12, md: 16 }, { xs: 8, sm: 12 }]}>
          {[
            {
              title: 'Completed Today',
              value: `${Object.values(habitStatuses).filter(status => status.completed).length}/${habits.length}`,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            },
            {
              title: 'Success Rate',
              value: `${habits.length > 0 ? Math.round((Object.values(habitStatuses).filter(status => status.completed).length / habits.length) * 100) : 0}%`,
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
            },
            {
              title: 'Active Streaks',
              value: Object.keys(analytics).filter(habitId => analytics[habitId]?.currentStreak > 0).length,
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
            },
            {
              title: 'Avg Performance',
              value: `${Math.round(Object.values(analytics).reduce((acc, curr) => acc + (curr?.completionRate || 0), 0) / Math.max(Object.keys(analytics).length, 1))}%`,
              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
            }
          ].map((stat, index) => (
            <Col xs={12} sm={12} md={6} key={index}>
              <Card 
                size="small" 
                style={{ 
                  textAlign: 'center', 
                  background: stat.background, 
                  color: 'white', 
                  borderRadius: 'clamp(0.5rem, 2vw, 0.75rem)', 
                  minHeight: 'clamp(4rem, 12vw, 5.25rem)', 
                  padding: 'clamp(0.25rem, 1vw, 0.5rem)',
                  border: 'none'
                }}
                bodyStyle={{ 
                  padding: 'clamp(0.5rem, 2vw, 0.75rem)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  height: '100%'
                }}
              >
                <div style={{ 
                  fontSize: 'clamp(1.25rem, 5vw, 1.5rem)', 
                  fontWeight: 'bold', 
                  marginBottom: 'clamp(0.125rem, 1vw, 0.25rem)' 
                }}>
                  {stat.value}
                </div>
                <div style={{ 
                  fontSize: 'clamp(0.625rem, 2vw, 0.75rem)', 
                  opacity: 0.9,
                  lineHeight: 1.2 
                }}>
                  {stat.title}
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      <Tabs 
        defaultActiveKey="log"
        activeKey={activeTab} 
        onChange={setActiveTab}
        size="large"
        style={{ fontSize: 'clamp(0.875rem, 2.5vw, 1rem)' }}
        items={[
          {
            key: 'log',
            label: (
              <Space>
                <EditOutlined />
                <span style={{ fontSize: 'clamp(0.875rem, 2.5vw, 1rem)' }}>Log Today</span>
              </Space>
            ),
            children: (
              <div style={{ marginTop: 'clamp(1rem, 3vw, 1.25rem)' }}>
                <Title level={4} style={{ fontSize: 'clamp(1rem, 3vw, 1.125rem)' }}>📝 Log Today's Progress</Title>
                <Text type="secondary" style={{ display: 'block', marginBottom: 'clamp(0.5rem, 2vw, 0.75rem)', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}>
                  Today is {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </Text>
                <Text type="secondary" style={{ display: 'block', marginBottom: 'clamp(1rem, 3vw, 1.5rem)', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}>
                  You have {getTodaysHabits().length} habit{getTodaysHabits().length !== 1 ? 's' : ''} scheduled for today.
                </Text>
                {getTodaysHabits().length > 0 ? (
                  <div>
                    {getTodaysHabits().map(habit => {
                      const status = habitStatuses[habit.id] || { completed: false };
                      const categoryInfo = getCategoryInfo(habit.category);
                      
                      return (
                        <Card
                          key={habit.id}
                          style={responsiveStyles.card}
                          bodyStyle={responsiveStyles.card}
                        >
                          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '0.25rem', background: `linear-gradient(90deg, ${categoryInfo.color}, ${habit.color})`, zIndex: 1 }} />
                          
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'clamp(0.5rem, 2vw, 0.75rem)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(0.5rem, 2vw, 0.75rem)', flex: 1 }}>
                              <div style={responsiveStyles.habitIcon}>
                                {habit.icon}
                              </div>
                              <div style={{ flex: 1 }}>
                                <Title level={5} style={{ margin: 0, color: habit.color, fontSize: 'clamp(0.875rem, 3vw, 1rem)' }}>
                                  {habit.name}
                                </Title>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(0.25rem, 1vw, 0.375rem)', marginTop: 'clamp(0.125rem, 1vw, 0.25rem)', flexWrap: 'wrap' }}>
                                  <Tag size="small" color={categoryInfo.color} style={{ fontSize: 'clamp(0.625rem, 2vw, 0.625rem)', padding: '0.125rem 0.375rem' }}>
                                    {categoryInfo.icon} {categoryInfo.label}
                                  </Tag>
                                  {habit.isQuantifiable && (
                                    <Tag size="small" color="green" style={{ fontSize: 'clamp(0.625rem, 2vw, 0.625rem)', padding: '0.125rem 0.375rem' }}>
                                      Target: {habit.targetCount}
                                    </Tag>
                                  )}
                                </div>
                              </div>
                            </div>

                            <Button
                              type={status.completed ? 'default' : 'primary'}
                              size="large"
                              shape="circle"
                              style={responsiveStyles.completionButton}
                              icon={<CheckCircleOutlined />}
                              onClick={() => handleToggleCompletion(habit.id, !status.completed)}
                            />
                          </div>

                          {habit.isQuantifiable && status.actualValue && (
                            <div style={{ marginTop: 'clamp(0.5rem, 2vw, 0.75rem)' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'clamp(0.25rem, 1vw, 0.375rem)' }}>
                                <Text style={{ fontSize: 'clamp(0.75rem, 2vw, 0.875rem)', color: '#666' }}>Target: {habit.targetCount}</Text>
                                <Text style={{ color: habit.color, fontWeight: 'bold', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}>✓ {status.actualValue} completed</Text>
                              </div>
                              <Progress 
                                percent={Math.min(100, Math.round((status.actualValue / habit.targetCount) * 100))} 
                                size="small" 
                                strokeColor={habit.color} 
                                showInfo={true} 
                              />
                            </div>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                ) : (
                  <Empty 
                    description="No habits scheduled for today" 
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  >
                    <Button 
                      type="primary" 
                      icon={<PlusOutlined />}
                      onClick={handleCreateHabit}
                      style={{ backgroundColor: '#1E90FF', borderColor: '#1E90FF' }}
                    >
                      Add Daily Habit
                    </Button>
                  </Empty>
                )}
              </div>
            )
          },
          {
            key: 'habits',
            label: (
              <Space>
                <CheckCircleOutlined />
                <span style={{ fontSize: 'clamp(0.875rem, 2.5vw, 1rem)' }}>My Habits</span>
                <Badge count={habits.length} style={{ backgroundColor: '#1E90FF', fontSize: 'clamp(0.625rem, 2vw, 0.75rem)' }} />
              </Space>
            ),
            children: (
              <div>
                {habits.length === 0 ? (
                  <Empty
                    description="No habits yet"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  >
                    <Button 
                      type="primary" 
                      icon={<PlusOutlined />}
                      onClick={handleCreateHabit}
                      style={{ backgroundColor: '#1E90FF', borderColor: '#1E90FF' }}
                    >
                      Create Your First Habit
                    </Button>
                  </Empty>
                ) : (
                  <div>
                    {/* Category headers with grouped habits */}
                    {habitCategories.map(category => {
                      const categoryHabits = habits.filter(habit => habit.category === category.key);
                      if (categoryHabits.length === 0) return null;
                      
                      return (
                        <div key={category.key} style={{ marginBottom: 'clamp(1.5rem, 4vw, 2rem)' }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            marginBottom: 'clamp(0.75rem, 2.5vw, 1rem)',
                            padding: 'clamp(0.5rem, 2vw, 0.75rem) clamp(0.75rem, 3vw, 1rem)',
                            background: `linear-gradient(90deg, ${getCategoryInfo(category.key).color}15, transparent)`,
                            borderRadius: 'clamp(0.5rem, 2vw, 0.75rem)',
                            border: `1px solid ${getCategoryInfo(category.key).color}30`,
                            gap: 'clamp(0.5rem, 2vw, 0.75rem)',
                            flexWrap: 'wrap'
                          }}>
                            <span style={{ fontSize: 'clamp(1.25rem, 5vw, 1.75rem)' }}>{getCategoryInfo(category.key).icon}</span>
                            <Title level={4} style={{ margin: 0, color: getCategoryInfo(category.key).color, fontSize: 'clamp(1rem, 3vw, 1.125rem)' }}>
                              {category.label}
                            </Title>
                            <Badge 
                              count={categoryHabits.length} 
                              style={{ backgroundColor: getCategoryInfo(category.key).color }}
                            />
                          </div>
                          <div>
                            {categoryHabits.map(habit => (
                              <Card
                                key={habit.id}
                                style={responsiveStyles.card}
                                bodyStyle={responsiveStyles.card}
                                actions={[
                                  <Button 
                                    type="text" 
                                    icon={<EditOutlined />} 
                                    onClick={() => handleEditHabit(habit)}
                                    style={{ color: habit.color }}
                                  >
                                    Edit
                                  </Button>,
                                  <Button 
                                    type="text" 
                                    icon={<BarChartOutlined />} 
                                    style={{ color: habit.color }}
                                  >
                                    Analytics
                                  </Button>,
                                  <Popconfirm
                                    title="Delete this habit?"
                                    description="This action cannot be undone."
                                    onConfirm={() => handleDeleteHabit(habit.id)}
                                    okText="Yes"
                                    cancelText="No"
                                  >
                                    <Button 
                                      type="text" 
                                      icon={<DeleteOutlined />} 
                                      danger
                                    >
                                      Delete
                                    </Button>
                                  </Popconfirm>
                                ]}
                              >
                                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: `linear-gradient(90deg, ${getCategoryInfo(habit.category).color}, ${habit.color})`, zIndex: 1 }} />

                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                  <div style={{ fontSize: '32px' }}>{habit.icon}</div>
                                  <div style={{ flex: 1 }}>
                                    <Title level={5} style={{ margin: 0, color: habit.color, fontSize: '16px', fontWeight: 'bold' }}>
                                      {habit.name}
                                    </Title>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                                      <Tag size="small" color={getCategoryInfo(habit.category).color} style={{ fontSize: '10px', padding: '2px 6px' }}>
                                        {getCategoryInfo(habit.category).icon} {getCategoryInfo(habit.category).label}
                                      </Tag>
                                      <Tag size="small" color="blue" style={{ fontSize: '10px', padding: '2px 6px' }}>
                                        {habit.frequency}
                                      </Tag>
                                      {habit.isQuantifiable && (
                                        <Tag size="small" color="green" style={{ fontSize: '10px', padding: '2px 6px' }}>
                                          Target: {habit.targetCount}
                                        </Tag>
                                      )}
                                    </div>
                                    {habit.description && (
                                      <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
                                        {habit.description}
                                      </Text>
                                    )}
                                  </div>
                                </div>
                              </Card>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )
          },
          {
            key: 'analytics',
            label: (
              <Space>
                <BarChartOutlined />
                <span style={{ fontSize: 'clamp(0.875rem, 2.5vw, 1rem)' }}>Analytics</span>
              </Space>
            ),
            children: (
              <div style={{ padding: '20px 0' }}>
                <Title level={3} style={{ color: '#1E90FF', marginBottom: '24px' }}>
                  📊 Comprehensive Analytics
                </Title>

                {habits.length === 0 ? (
                  <Empty
                    description="No analytics data yet"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  >
                    <Button 
                      type="primary" 
                      icon={<PlusOutlined />}
                      onClick={handleCreateHabit}
                      style={{ backgroundColor: '#1E90FF', borderColor: '#1E90FF' }}
                    >
                      Create Your First Habit
                    </Button>
                  </Empty>
                ) : (
                  <div>
                    {/* Overall Statistics */}
                    <Card title="📈 Overall Performance" style={{ marginBottom: '24px' }}>
                      <Row gutter={[16, 16]}>
                        <Col xs={12} sm={6}>
                          <Statistic 
                            title="Total Habits" 
                            value={habits.length} 
                            valueStyle={{ color: '#1890ff' }}
                            prefix={<TrophyOutlined />}
                          />
                        </Col>
                        <Col xs={12} sm={6}>
                          <Statistic 
                            title="Completed Today" 
                            value={Object.values(habitStatuses).filter(status => status.completed).length} 
                            suffix={`/ ${getTodaysHabits().length}`}
                            valueStyle={{ color: '#52c41a' }}
                            prefix={<CheckCircleOutlined />}
                          />
                        </Col>
                        <Col xs={12} sm={6}>
                          <Statistic 
                            title="Active Streaks" 
                            value={Object.keys(analytics).filter(habitId => analytics[habitId]?.currentStreak > 0).length} 
                            valueStyle={{ color: '#fa8c16' }}
                            prefix={<FireOutlined />}
                          />
                        </Col>
                        <Col xs={12} sm={6}>
                          <Statistic 
                            title="Avg Performance" 
                            value={Math.round(Object.values(analytics).reduce((acc, curr) => acc + (curr?.completionRate || 0), 0) / Math.max(Object.keys(analytics).length, 1))} 
                            suffix="%" 
                            valueStyle={{ color: '#722ed1' }}
                            prefix={<BarChartOutlined />}
                          />
                        </Col>
                      </Row>
                    </Card>

                    {/* Habit Performance Ranking */}
                    <Card title="🏆 Habit Performance Ranking" style={{ marginBottom: '24px' }}>
                      {habits.length > 0 ? (
                        <List
                          dataSource={habits.map(habit => {
                            const habitAnalytics = analytics[habit.id] || {};
                            return {
                              ...habit,
                              completionRate: habitAnalytics.completionRate || 0,
                              currentStreak: habitAnalytics.currentStreak || 0,
                              totalCompleted: habitAnalytics.totalCompleted || 0
                            };
                          }).sort((a, b) => b.completionRate - a.completionRate)}
                          renderItem={(item, index) => (
                            <List.Item>
                              <List.Item.Meta
                                avatar={
                                  <Badge count={index + 1} style={{ backgroundColor: index < 3 ? '#faad14' : '#d9d9d9' }}>
                                    <Avatar style={{ backgroundColor: 'transparent', fontSize: '20px' }}>
                                      {item.icon}
                                    </Avatar>
                                  </Badge>
                                }
                                title={
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span>{item.name}</span>
                                    <Tag color={getCategoryInfo(item.category).color} size="small">
                                      {getCategoryInfo(item.category).label}
                                    </Tag>
                                  </div>
                                }
                                description={
                                  <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                                    <span>Success Rate: <Text strong style={{ color: item.color }}>{item.completionRate}%</Text></span>
                                    <span>Streak: <Text strong>{item.currentStreak} days</Text></span>
                                    <span>Total: <Text strong>{item.totalCompleted}</Text></span>
                                  </div>
                                }
                              />
                              <div style={{ width: '100px' }}>
                                <Progress 
                                  percent={item.completionRate} 
                                  size="small" 
                                  strokeColor={item.color}
                                  showInfo={false}
                                />
                              </div>
                            </List.Item>
                          )}
                        />
                      ) : (
                        <Empty description="No habit data available yet. Start logging your habits!" />
                      )}
                    </Card>
                  </div>
                )}
              </div>
            )
          }
        ]}
      />

      <Modal
        title={isEditing ? "Edit Habit" : "Create New Habit"}
        open={showHabitModal}
        onCancel={() => { 
          setShowHabitModal(false); 
          setShowTemplates(false); 
          setCreationMode(''); 
          form.resetFields(); 
          setIsEditing(false);
          setSelectedHabit(null);
        }}
        footer={null}
        className="habit-tracker-modal"
      >
        {!isEditing && !creationMode && (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Title level={4} style={{ marginBottom: '32px', color: '#595959' }}>How would you like to create your habit?</Title>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Card 
                  hoverable 
                  style={{ 
                    textAlign: 'center', 
                    minHeight: '180px', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    justifyContent: 'center', 
                    border: '2px solid #f0f0f0',
                    cursor: 'pointer'
                  }} 
                  onClick={() => { setCreationMode('template'); setShowTemplates(true); }}
                  bodyStyle={{ padding: '24px 16px' }}
                >
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
                  <Title level={4} style={{ margin: '0 0 8px 0', fontSize: '18px' }}>Use Template</Title>
                  <Text type="secondary" style={{ fontSize: '14px' }}>Choose from pre-built habits</Text>
                </Card>
              </Col>
              <Col xs={24} sm={12}>
                <Card 
                  hoverable 
                  style={{ 
                    textAlign: 'center', 
                    minHeight: '180px', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    justifyContent: 'center', 
                    border: '2px solid #f0f0f0',
                    cursor: 'pointer'
                  }} 
                  onClick={() => { setCreationMode('custom'); }}
                  bodyStyle={{ padding: '24px 16px' }}
                >
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>✨</div>
                  <Title level={4} style={{ margin: '0 0 8px 0', fontSize: '18px' }}>Create Custom</Title>
                  <Text type="secondary" style={{ fontSize: '14px' }}>Build your own habit</Text>
                </Card>
              </Col>
            </Row>
          </div>
        )}

        {creationMode === 'template' && showTemplates && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <Title level={4} style={{ margin: 0 }}>📋 Choose from Templates</Title>
              <Button icon={<LeftOutlined />} onClick={() => { setCreationMode(''); setShowTemplates(false); }}>Back</Button>
            </div>
            
            {/* Search and Filter */}
            <Row gutter={16} style={{ marginBottom: '20px' }}>
              <Col flex="auto">
                <Input 
                  placeholder="Search templates..." 
                  prefix={<SearchOutlined />} 
                  value={templateSearch} 
                  onChange={(e) => setTemplateSearch(e.target.value)} 
                  allowClear 
                  size="large"
                />
              </Col>
              <Col>
                <Select 
                  value={templateCategory} 
                  onChange={setTemplateCategory} 
                  style={{ width: 180 }}
                  size="large"
                >
                  <Option value="all">All Categories</Option>
                  {HABIT_CATEGORIES.map(cat => (
                    <Option key={cat.key} value={cat.key}>
                      {lookupIcon(cat.iconId).emoji} {cat.label}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>
            
            {/* Beautiful Template Cards Grid */}
            <div style={{ maxHeight: '500px', overflowY: 'auto', padding: '10px' }}>
              <Row gutter={[16, 16]}>
                {getFilteredTemplates().map((template, index) => {
                  const templateIcon = lookupIcon(template.iconId);
                  const templateColor = lookupColor(template.colorId);
                  const categoryInfo = lookupCategory(template.category);
                  
                  return (
                    <Col xs={24} sm={12} md={8} key={template.id || index}>
                      <Card
                        hoverable
                        style={{ 
                          ...responsiveStyles.card,
                          background: `linear-gradient(90deg, ${categoryInfo.color}15, ${templateColor}30)`,
                          border: `1px solid ${categoryInfo.color}30`
                        }}
                        bodyStyle={responsiveStyles.card}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-4px)';
                          e.currentTarget.style.boxShadow = `0 8px 24px ${templateColor}30`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                        }}
                      >
                        {/* Color accent bar */}
                        <div 
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '4px',
                            background: `linear-gradient(90deg, ${categoryInfo.color}, ${templateColor})`
                          }}
                        />
                        
                        {/* Header */}
                        <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                          <div style={responsiveStyles.templateIcon}>
                            {templateIcon.emoji}
                          </div>
                          <Title level={5} style={responsiveStyles.cardTitle}>
                            {template.name}
                          </Title>
                        </div>
                        
                        {/* Content */}
                        <div style={{ flex: 1, textAlign: 'center' }}>
                          <Text 
                            type="secondary" 
                            style={responsiveStyles.bodyText}
                          >
                            {template.description}
                          </Text>
                          
                          {/* Tags */}
                          <div style={{ marginBottom: '8px' }}>
                            <Tag size="small" color={categoryInfo.color} style={{ fontSize: 'clamp(0.625rem, 2vw, 0.625rem)', padding: '0.125rem 0.375rem' }}>
                              {categoryInfo.icon} {categoryInfo.label}
                            </Tag>
                            <Tag size="small" color="blue" style={{ fontSize: 'clamp(0.625rem, 2vw, 0.625rem)', padding: '0.125rem 0.375rem' }}>
                              {template.frequency}
                            </Tag>
                            {template.isQuantifiable && (
                              <Tag size="small" color="green" style={{ fontSize: 'clamp(0.625rem, 2vw, 0.625rem)', padding: '0.125rem 0.375rem' }}>
                                Target: {template.targetCount}
                              </Tag>
                            )}
                          </div>
                        </div>
                        
                        {/* Action Button */}
                        <Button
                          type="primary"
                          size="small"
                          style={responsiveStyles.primaryButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUseTemplate(template);
                          }}
                        >
                          Add to My Habits
                        </Button>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
              
              {/* Empty State */}
              {getFilteredTemplates().length === 0 && (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '60px 20px',
                  color: '#999'
                }}>
                  <SearchOutlined style={{ fontSize: '48px', marginBottom: '16px', color: '#d9d9d9' }} />
                  <div style={{ fontSize: '16px', marginBottom: '8px' }}>No templates found</div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Try adjusting your search or category filter
                  </Text>
                </div>
              )}
            </div>
          </div>
        )}

        {(creationMode === 'custom' || isEditing) && (
          <div>
            {!isEditing && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <Title level={4} style={{ margin: 0 }}>Create Custom Habit</Title>
                <Button icon={<LeftOutlined />} onClick={() => { setCreationMode(''); form.resetFields(); }}>Back</Button>
              </div>
            )}
            {isEditing && (
              <div style={{ marginBottom: '16px' }}>
                <Title level={4} style={{ margin: 0 }}>Edit Habit Details</Title>
              </div>
            )}
            
            <Form form={form} layout="vertical" onFinish={handleHabitSubmit} className="habit-form" validateTrigger="onChange">
              <Row gutter={16}>
                <Col span={16}>
                  <Form.Item 
                    label="Habit Name" 
                    name="name" 
                    rules={[
                      { required: true, message: 'Please enter habit name' }, 
                      { min: 2, message: 'Must be at least 2 characters' }, 
                      { max: 50, message: 'Must be less than 50 characters' }
                    ]}
                  >
                    <Input placeholder="e.g., Drink 8 glasses of water" maxLength={50} showCount />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item 
                    label="Icon" 
                    name="icon" 
                    rules={[{ required: true, message: 'Please select an icon' }]}
                  >
                    <Select 
                      placeholder="Select icon" 
                      showSearch 
                      optionFilterProp="children" 
                      style={{ width: '100%' }} 
                      dropdownStyle={{ maxHeight: 200, overflow: 'auto' }}
                      allowClear
                      filterOption={(input, option) => {
                        const iconData = HABIT_ICONS[option.value];
                        if (!iconData) return false;
                        return (
                          iconData.name.toLowerCase().includes(input.toLowerCase()) ||
                          iconData.emoji.includes(input) ||
                          (iconData.tags && iconData.tags.some(tag => tag.toLowerCase().includes(input.toLowerCase())))
                        );
                      }}
                    >
                      {Object.entries(HABIT_ICONS).map(([iconId, iconData]) => (
                        <Option key={iconId} value={iconId}>
                          <span style={{ fontSize: '18px', marginRight: '8px' }}>{iconData.emoji}</span>
                          <span style={{ fontSize: '12px', color: '#666' }}>{iconData.name}</span>
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              
              <Form.Item label="Description (Optional)" name="description">
                <TextArea rows={2} placeholder="Brief description..." maxLength={200} showCount />
              </Form.Item>
              
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item 
                    label="Category" 
                    name="category" 
                    rules={[{ required: true, message: 'Please select a category' }]}
                  >
                    <Select placeholder="Select category">
                      {HABIT_CATEGORIES.map(category => (
                        <Option key={category.key} value={category.key}>
                          {lookupIcon(category.iconId).emoji} {category.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item 
                    label="Frequency" 
                    name="frequency" 
                    rules={[{ required: true, message: 'Please select frequency' }]}
                  >
                    <Select placeholder="Every Day">
                      {HABIT_FREQUENCIES.map(freq => (
                        <Option key={freq.value} value={freq.value}>{freq.label}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item 
                    label="Theme Color" 
                    name="color" 
                    rules={[{ required: true, message: 'Please select a color' }]}
                  >
                    <Select placeholder="Select color">
                      {HABIT_COLORS.map(color => (
                        <Option key={color.id} value={color.id}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ width: '16px', height: '16px', backgroundColor: color.value, borderRadius: '2px', border: '1px solid #d9d9d9' }} />
                            {color.name}
                          </div>
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Difficulty Level" name="difficulty">
                    <Rate allowHalf />
                  </Form.Item>
                </Col>
              </Row>
              
              <Form.Item name="isQuantifiable" valuePropName="checked">
                <Checkbox>This is a quantifiable habit (track specific amounts)</Checkbox>
              </Form.Item>
              
              <Form.Item dependencies={['isQuantifiable']}>
                {({ getFieldValue }) => {
                  return getFieldValue('isQuantifiable') ? (
                    <Form.Item 
                      label="Target Amount" 
                      name="targetCount" 
                      rules={[
                        { required: true, message: 'Please enter target amount' }, 
                        { type: 'number', min: 1, message: 'Target must be at least 1' }
                      ]}
                    >
                      <InputNumber 
                        min={1} 
                        max={999} 
                        placeholder="8" 
                        style={{ width: '100%' }}
                        addonAfter="times/amount (specify in description)"
                      />
                    </Form.Item>
                  ) : null;
                }}
              </Form.Item>
              
              <Form.Item label="Daily Reminder (Optional)" name="reminderTime">
                <TimePicker format="HH:mm" placeholder="Set reminder time" style={{ width: '100%' }} />
              </Form.Item>
              
              <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Space>
                  <Button onClick={() => { 
                    setShowHabitModal(false); 
                    setCreationMode(''); 
                    setIsEditing(false);
                    setSelectedHabit(null);
                    form.resetFields(); 
                  }}>
                    Cancel
                  </Button>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    {isEditing ? 'Update Habit' : 'Create Habit'}
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>

      {showCompletionModal && (
        <Modal
          title="Log Progress"
          open={showCompletionModal}
          onCancel={() => setShowCompletionModal(false)}
          footer={null}
        >
          <Form 
            form={completionForm} 
            onFinish={async (values) => {
              try {
                const habit = habits.find(h => h.id === completionData.habitId);
                const actualValue = values.actualValue || 1;
                await logHabitCompletion(user.uid, habit.id, new Date(), true, { 
                  actualValue, 
                  targetValue: habit.targetCount, 
                  completionPercentage: Math.round((actualValue / habit.targetCount) * 100) 
                });
                setHabitStatuses(prev => ({ 
                  ...prev, 
                  [habit.id]: { 
                    completed: true, 
                    actualValue, 
                    completionPercentage: Math.round((actualValue / habit.targetCount) * 100) 
                  } 
                }));
                notification.success({ message: '🎉 Progress logged!', description: `You completed ${actualValue}!` });
                setShowCompletionModal(false);
                completionForm.resetFields();
              } catch (error) {
                notification.error({ message: 'Error logging progress', description: error.message });
              }
            }}
          >
            <Form.Item 
              label="How much did you complete?" 
              name="actualValue" 
              rules={[{ required: true, message: 'Please enter the amount' }]}
            >
              <InputNumber min={0} style={{ width: '100%' }} placeholder="Enter amount" />
            </Form.Item>
            <Form.Item style={{ textAlign: 'right' }}>
              <Space>
                <Button onClick={() => setShowCompletionModal(false)}>Cancel</Button>
                <Button type="primary" htmlType="submit">Log Progress</Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      )}
    </div>
  );
};

export default HabitTrackerApp; 